import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../App";
import { Users, Award, Target } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { pageTemplates } from "../utils/pageTemplates";
import { SeoContentSection } from "../components/Seo";
import { OMOS_INTRO } from "../data/homeContent";
import { INK, PRIMARY, Overline, AuroraBlue, AuroraLight, Reveal, Counter } from "../components/shared/kursusUi";

const getSection = (sections, type) => sections?.find(s => s.type === type);

const iconMap = {
  target: Target,
  users: Users,
  award: Award
};

const AboutPage = () => {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/pages/om-os`);
      setPageData(response.data);
    } catch (error) {
      setPageData(pageTemplates["om-os"]);
    }
  };

  const data = pageData || pageTemplates["om-os"];
  const hero = getSection(data.sections, 'hero');
  const mission = getSection(data.sections, 'text');
  const rawStats = getSection(data.sections, 'stats');
  // Clean legacy stats from older DB versions: drop "Kursister årligt" and update outdated 15+ → 10+
  const stats = rawStats ? {
    ...rawStats,
    items: (rawStats.items || [])
      .filter(item => item?.label !== 'Kursister årligt')
      .map(item => item?.label === 'Års erfaring' && item?.value === '15+' ? { ...item, value: '10+' } : item)
  } : null;
  const values = getSection(data.sections, 'values');
  // Strip legacy <p>...</p> wrappers from mission content
  const missionContent = mission?.content
    ? mission.content.replace(/^\s*<p>/i, '').replace(/<\/p>\s*$/i, '').trim()
    : mission?.content;


  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'hsl(210, 20%, 98%)' }}>
      <div className="votra-grain" aria-hidden />
      <Header />

      {/* Hero — dark ink + blue aurora */}
      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: '#F4F6FB' }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <Reveal><Overline light>Om os</Overline></Reveal>
            <Reveal i={1}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-[1.05] mb-6">
                {hero?.title || "Om Votra Vognmandsuddannelser"}
              </h1>
            </Reveal>
            {hero?.description && (
              <Reveal i={2}>
                <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl">
                  {hero.description}
                </p>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-16 md:py-20 flex-1">
        <div className="max-w-4xl mx-auto">
          {/* Mission — editorial pull block */}
          {mission && (
            <Reveal className="mb-14">
              <Overline>{mission.title}</Overline>
              <p className="font-display text-2xl md:text-3xl leading-snug text-slate-800 whitespace-pre-line border-l-2 pl-6" style={{ borderColor: PRIMARY }}>
                {missionContent}
              </p>
            </Reveal>
          )}
        </div>

        {/* Stats — dark band with count-up */}
        {stats && stats.items?.length > 0 && (
          <Reveal className="max-w-5xl mx-auto mb-16 rounded-3xl overflow-hidden relative" style={{ backgroundColor: INK }}>
            <AuroraBlue />
            <div className="relative z-10 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
              {stats.items.map((stat, idx) => (
                <div key={idx} className="p-10 text-center">
                  <p className="font-display text-4xl md:text-5xl font-medium text-white">
                    <Counter value={stat.value} />
                  </p>
                  <p className="text-slate-300 text-sm mt-2 uppercase tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        )}

        {/* Values — glass cards */}
        {values && values.items?.length > 0 && (
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.items.map((value, idx) => {
              const IconComponent = iconMap[value.icon] || Target;
              return (
                <Reveal
                  i={idx}
                  key={idx}
                  className="rounded-2xl p-7 bg-white border border-slate-200/80 shadow-[0_8px_30px_rgba(15,40,90,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_46px_rgba(15,40,90,0.14)]"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: 'hsl(210,100%,45%,0.1)' }}>
                    <IconComponent className="h-6 w-6" style={{ color: PRIMARY }} />
                  </div>
                  <h4 className="font-display text-xl font-medium mb-2 text-slate-900">
                    {value.title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed text-sm">
                    {value.description}
                  </p>
                </Reveal>
              );
            })}
          </div>
        )}

        {/* Samarbejde link */}
        <div className="max-w-5xl mx-auto mt-10">
          <Link
            to="/udlicitering"
            className="text-sm text-slate-500 hover:text-[hsl(210,100%,45%)] hover:underline transition-colors"
            data-testid="about-samarbejde-link"
          >
            Samarbejde
          </Link>
        </div>
      </div>

      <SeoContentSection intro={OMOS_INTRO} testid="omos-seo-content" />

      <Footer />
    </div>
  );
};

export default AboutPage;
